import { SubscriptionStatus } from "~/types/Subscriptions";
import type { CartCouponMode, PriceVariantKey } from "~/types/Checkout";

export const CouponDurationTypeEnum = {
    ONE_TIME: "one_time",
    FOREVER: "forever",
} as const;

export type CouponDurationType =
    (typeof CouponDurationTypeEnum)[keyof typeof CouponDurationTypeEnum];

export type Coupon = {
    appliesToProducts?: string[];
    couponId?: string | null;
    discountAmount?: number;
    discountPercentage?: number;
    valid?: boolean;
    errorMessage?: string;
    durationType?: CouponDurationType;
};

export type ChargeBeePriceItem = {
    slug: string;
    faceCreamSlug: string;
    id?: string;
    title?: string;
    price: number;
    /** Active test prices in cents. The API returns an empty array when none are active. */
    priceVariants?: ChargeBeePriceVariant[];
};

export type ChargeBeePriceVariant = {
    key: PriceVariantKey;
    /** Price in cents. */
    price: number;
    name?: string;
};

export type ChargeBeeProductDetails = {
    priceItemId: string;
    productFamily: string;
    sku: string;
    selectedPriceItemId?: string | null;
    price: number;
    title: string;
    imageUrl: string;
    slug?: string;
    priceItems?: ChargeBeePriceItem[];
    selectedPriceItem?: ChargeBeePriceItem;
    upgradedFromPriceItemId?: string;
    couponMode?: CartCouponMode;
    explicitCouponId?: string;
    priceVariantKey?: PriceVariantKey;
};

/**
 * Reports on a wallet payment as ChargeBee walks through it.
 *
 * These are not an alternative to the `handlePayment()` promise but the only
 * complete account of the payment. ChargeBee builds the Apple Pay promise with
 * no reject path at all, so a dismissed or refused sheet leaves it pending for
 * good and is reported here and nowhere else. The promise still resolves on
 * success, which is where the order submit stays.
 */
export interface ChargebeeWalletCallbacks {
    /** The wallet button was tapped, before its sheet takes over the screen. */
    click?: () => void;
    /** The user dismissed the wallet sheet without paying. */
    cancel?: () => void;
    error?: (err: unknown) => void;
    success?: (result: ChargebeePaymentResult) => void;
}

export interface ChargebeePaymentHandler {
    paymentIntent: { id: string } | undefined;
    setPaymentIntent(intent: unknown): Promise<void>;
    updatePaymentIntent(intent: unknown): Promise<void>;
    mountPaymentButton(selector: string, ...options: unknown[]): Promise<void>;
    handlePayment(callbacks?: ChargebeeWalletCallbacks): Promise<ChargebeePaymentResult>;
}

export const IntentUpdateResultEnum = {
    UPDATED: "updated",
    RECREATED: "recreated",
    FAILED: "failed",
} as const;

export type IntentUpdateResult =
    (typeof IntentUpdateResultEnum)[keyof typeof IntentUpdateResultEnum];

export interface ChargebeePaymentResult {
    paymentIntent?: {
        id?: string;
        payer_info?: {
            customer?: {
                email?: string;
                firstName?: string;
                lastName?: string;
                /**
                 * ChargeBee's Apple Pay handler puts the shipping contact's
                 * phone in the `customer` object it confirms the intent with.
                 * Never populated for Google Pay, whose phone arrives on
                 * `shipping_address` instead.
                 */
                phone?: string;
            };
            /**
             * Express Google Pay's phone lands here as `phone` — see
             * `googlePayShippingPhone` for how it is requested and renamed out
             * of Google's `phoneNumber`.
             */
            shipping_address?: Record<string, string>;
            billing_address?: Record<string, string>;
            phone_number?: string;
        };
    };
}

export interface ChargebeeInstance {
    load(handlerType: string): Promise<ChargebeePaymentHandler>;
}

export interface ChargebeeSDK {
    init(config: { site?: string; publishableKey?: string }): void;
    getInstance(): ChargebeeInstance;
}

declare global {
    interface Window {
        Chargebee?: ChargebeeSDK;
        ApplePaySession?: {
            canMakePayments(): boolean;
        };
    }
}

export const PaymentMethodEnum = {
    CARD: "card",
    GOOGLE_PAY: "google_pay",
    APPLE_PAY: "apple_pay",
} as const;

/**
 * Splits the express wallet checkout (Apple/Google Pay on step 1, which
 * replaces the form entirely) from the regular form checkout on every purchase
 * event. Carried by `purchase_cta_click`, `purchase_attempt` and `purchase`.
 */
export const CheckoutTypeEnum = {
    REGULAR: "regular",
    EXPRESS: "express",
} as const;

export type CheckoutType =
    (typeof CheckoutTypeEnum)[keyof typeof CheckoutTypeEnum];

export type CustomerInvoiceProductData = {
    productName: string;
    priceItemSlug: string;
    priceItemName: string;
    productFamily: string;
    productImage: string;
};

export const InvoiceStatusEnum = {
    SUCCESSFUL: "Successful",
    FAILED: "Failed",
    REFUNDED: "Refunded",
    VOIDED: "Voided",
} as const;

export type InvoiceStatus = (typeof InvoiceStatusEnum)[keyof typeof InvoiceStatusEnum];

export const InvoiceChargeTypeEnum = {
    SUBSCRIPTION: "subscription",
    ONE_TIME: "one_time",
} as const;

export type InvoiceChargeType = (typeof InvoiceChargeTypeEnum)[keyof typeof InvoiceChargeTypeEnum];

export type CustomerInvoiceData = {
    id: string;
    status: InvoiceStatus;
    chargeType: InvoiceChargeType;
    products: CustomerInvoiceProductData[];
    amountPaid: number;
    amountTotal: number;
    createdAt: string | null;
    paidAt: string | null;
    // Not returned by the backend yet; the Billing & orders page groups by `orderId` once it is.
    orderId?: string | null;
    paymentsInOrder?: number | null;
    shippedAt?: string | null;
    // As they were on the invoice, not the patient's current card or name. Not returned yet; the
    // invoice page leaves out a row whose value is missing.
    paymentMethod?: string | null;
    billedTo?: string | null;
    planName?: string | null;
    billingPeriodStartsAt?: string | null;
    billingPeriodEndsAt?: string | null;
    nextBillingAt?: string | null;
    note?: string | null;
    // What the patient's credit and any promo discounts took off this invoice, in dollars, one total each.
    // Zero when there was none; nothing shows for a zero.
    promotionalCreditsAppliedTotal?: number | null;
    discountAppliedTotal?: number | null;
};

// A refund of a paid invoice; `amount` is in dollars, as the backend confirmed. Credit the patient earned and
// had applied to an invoice is never one of these. `downloadLink` expires about an hour after `/billing-data`
// issues it.
export type CustomerCreditNoteData = {
    number: string;
    invoiceId: string;
    amount: number;
    refundedAt: string | null;
    refundedVia: string | null;
    reason: string | null;
    downloadLink: string | null;
};

export type CustomerNextPaymentData = {
    nextPaymentDueAt: Date | null;
    nextPaymentAmount: number | null;
    products: CustomerInvoiceProductData[];
    subscriptionId: string;
};

/**
 * Subscription snapshot returned inline by the checkout endpoints.
 *
 * The backend creates the real subscription record asynchronously, so
 * `/auth/me` and `/billing-data` stay stale for an unbounded period after a
 * successful charge. This payload is the only source able to describe the
 * purchase immediately. It carries no id — the sibling `externalSubscriptionId`
 * on the checkout response is the identity key.
 */
export type PendingCheckoutSubscription = {
    status: SubscriptionStatus;
    priceItemSlug: string;
    nextPaymentDueAt: string | null;
    nextPaymentAmount: number | null;
    product: {
        imageUrl: string;
        productFamily: string;
    };
};

/** Fields both checkout endpoints add to describe the just-completed purchase. */
export type PendingCheckoutPurchase = {
    externalSubscriptionId?: string | null;
    pendingSubscription?: PendingCheckoutSubscription;
    pendingOneTimePurchaseInvoice?: CustomerInvoiceData;
};

/**
 * Both checkout endpoints answer a repeat of a checkout they already processed
 * with the original response instead of charging again.
 */
export type CheckoutDuplicateResponse = {
    /** Set when nothing new was created: this is the original checkout. */
    duplicateRequest?: boolean;
    /**
     * The `checkoutFlowId` the processed checkout was sent with: on a
     * duplicate, the original's, not the repeat's. Absent when that request
     * carried none; read `null` the same way.
     */
    checkoutFlowId?: string | null;
};

/** Sent with both checkout requests, see `checkoutFlowId.ts`. */
export type CheckoutFlowRequest = {
    /** Opaque to the backend: stored on the checkout log and echoed back, never validated as an id. */
    checkoutFlowId?: string;
};

/**
 * An invoice as the account UI sees it, tagged with whether the server has
 * materialized it yet.
 *
 * A pending invoice exists only as a checkout overlay, so nothing can be
 * fetched by its id — the invoice PDF above all. Anything rendering a
 * document action must check this flag first.
 */
export type BillingInvoice = CustomerInvoiceData & {
    isPending: boolean;
};

/** Shape the account UI consumes: `/billing-data` plus the pending overlay. */
export type BillingData = {
    invoices: BillingInvoice[];
    subscriptionNextBillingItems: CustomerNextPaymentData[];
    creditNotes?: CustomerCreditNoteData[];
};

export type PaymentMethodType =
    (typeof PaymentMethodEnum)[keyof typeof PaymentMethodEnum];
export type WalletPaymentMethodType = Exclude<
    PaymentMethodType,
    typeof PaymentMethodEnum.CARD
>;

export type ChargeBeePaymentSourceData = {
    id: string;
    type: string;
    cardBrand?: string;
    cardMaskedNumber?: string;
    cardExpiryMonth?: number;
    cardExpiryYear?: number;
};

export type PaymentUserData = {
    name: string;
    email: string;
    phone: string;
    dateOfBirth: string;
} | null;

export type Address = {
    line1: string | null;
    line2: string | null;
    city: string | null;
    postal_code: string | null;
    state: string | null;
    country?: string | null;
};

export type BillingAddress = Address & {
    fullName: string | null;
    phone: string | null;
};

/**
 * Immutable pricing context captured when a payment intent is created.
 * The checkout submit must use this coupon instead of re-reading mutable
 * checkout state after an asynchronous 3DS challenge.
 */
export type PaymentAttemptSnapshot = Readonly<{
    amountCents: number;
    couponId: string | null;
}>;

export type PaymentSubmitHandler = ({
    userData,
    paymentMethods,
    paymentIntentId,
    paymentTokenId,
    address,
    paymentType,
    attemptId,
    paymentAttempt,
}: {
    userData: PaymentUserData;
    address: Address | null;
    paymentMethods: string;
    paymentIntentId?: string;
    paymentTokenId?: string;
    paymentType?: "express_payment";
    /** Tokenize-stage attempt id reused for funnel log correlation. */
    attemptId?: string;
    /** Pricing context bound to the payment intent before 3DS starts. */
    paymentAttempt?: PaymentAttemptSnapshot;
}) => Promise<void>;
